/**
 * Construção segura de links de navegação do Google Maps.
 * Módulo puro: valida coordenadas e origem antes de gerar qualquer URL e
 * nunca devolve um link partido.
 */

export type LatLng = { lat: number; lng: number };

export type RoutePoint = LatLng & { name?: string | null };

function googleTravelMode(mode?: string): string {
  if (mode === "car" || mode === "motorcycle") return "driving";
  if (mode === "bike" || mode === "scooter") return "bicycling";
  if (mode === "transit" || mode === "train") return "transit";
  return "walking";
}

export function isValidLatLng(p: unknown): p is LatLng {
  if (!p || typeof p !== "object") return false;
  const { lat, lng } = p as LatLng;
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/**
 * Devolve um URL de direções a partir da posição do utilizador para o ponto
 * de partida. Cai para uma pesquisa por nome quando faltam coordenadas
 * válidas e devolve null quando não há nada utilizável.
 */
export function buildNavigationUrl(input: {
  userPosition?: LatLng | null;
  startPoint?: LatLng | null;
  startPointName?: string | null;
}): string | null {
  const { userPosition, startPoint, startPointName } = input;
  const validStart = isValidLatLng(startPoint) ? startPoint : null;
  const validUser = isValidLatLng(userPosition) ? userPosition : null;

  if (validStart && validUser) {
    const params = new URLSearchParams({
      api: "1",
      origin: `${validUser.lat},${validUser.lng}`,
      destination: `${validStart.lat},${validStart.lng}`,
      travelmode: "walking",
    });
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  }
  if (validStart) {
    const params = new URLSearchParams({
      api: "1",
      destination: `${validStart.lat},${validStart.lng}`,
      travelmode: "walking",
    });
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  }
  const name = startPointName?.trim();
  if (name) {
    const params = new URLSearchParams({ api: "1", query: name });
    return `https://www.google.com/maps/search/?${params.toString()}`;
  }
  return null;
}

/**
 * Google Maps directions preview for an ordered day route.
 *
 * `avoidHighways` existe para experiências de estrada panorâmica (Nacional 2,
 * Route 66, NC500…): sem ele o Google desvia para a autoestrada e a etapa
 * deixa de ser feita pela estrada pedida.
 */
export function buildRoutePreviewUrl(input: {
  stops: RoutePoint[];
  mode?: string;
  origin?: LatLng | null;
  avoidHighways?: boolean;
}): string | null {
  const stops = input.stops.filter(isValidLatLng);
  if (stops.length === 0) return null;
  const origin = isValidLatLng(input.origin) ? input.origin : stops[0];
  const destination = stops.length > 1 ? stops[stops.length - 1] : stops[0];
  const params = new URLSearchParams({
    api: "1",
    origin: `${origin.lat},${origin.lng}`,
    destination: `${destination.lat},${destination.lng}`,
    travelmode: googleTravelMode(input.mode),
  });
  const firstWaypoint = input.origin ? 0 : 1;
  const intermediate = stops.slice(firstWaypoint, -1);
  // O Maps aceita no máximo nove pontos intermédios neste URL. Em corredores
  // densos, amostramos toda a etapa em vez de cortar os últimos pontos — assim
  // a rota continua presa ao traçado até ao destino final.
  const selectedWaypoints = intermediate.length <= 9
    ? intermediate
    : Array.from({ length: 9 }, (_, index) =>
        intermediate[Math.round((index * (intermediate.length - 1)) / 8)],
      );
  const waypoints = selectedWaypoints
    .map((stop) => `${stop.lat},${stop.lng}`)
    .join("|");
  if (waypoints) params.set("waypoints", waypoints);
  if (input.avoidHighways) {
    // O parâmetro documentado do Maps URLs usa vírgulas; `dirflg` é o legado
    // que a app móvel ainda respeita. Enviamos ambos para que a opção
    // "evitar autoestradas" venha já ligada, sem passos manuais.
    params.set("avoid", "highways,tolls,ferries");
    params.set("dirflg", "htf");
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export async function copyRouteUrl(url: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
    const textarea = document.createElement("textarea");
    textarea.value = url;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    textarea.remove();
    return copied;
  } catch {
    return false;
  }
}

/**
 * Links de navegação por segmento multimodal. Uma rota com vários meios de
 * transporte NÃO cabe num único link do Maps: geramos um link por grupo de
 * segmentos consecutivos com o mesmo modo.
 */
export function buildModeSegmentUrls(
  segments: Array<{ mode: string; label: string; stops: RoutePoint[] }>,
): Array<{ mode: string; label: string; from: string; to: string; url: string }> {
  const out: Array<{ mode: string; label: string; from: string; to: string; url: string }> = [];
  for (const segment of segments) {
    const url = buildRoutePreviewUrl({
      stops: segment.stops,
      mode: segment.mode,
      origin: isValidLatLng(segment.stops[0]) ? segment.stops[0] : null,
    });
    if (!url) continue;
    out.push({
      mode: segment.mode,
      label: segment.label,
      from: segment.stops[0]?.name ?? "",
      to: segment.stops[segment.stops.length - 1]?.name ?? "",
      url,
    });
  }
  return out;
}
